import { fetchAuthSession } from "@aws-amplify/auth";

import { getIdToken } from "../auth/tokens";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** A non-2xx response, carrying the parsed body so a view can show the reason. */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }

  /** The API's own explanation (FastAPI's `detail`), when it sent one as a string. */
  get detail(): string | null {
    const body = this.body;
    if (typeof body === "object" && body !== null && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
    return null;
  }
}

async function authedFetch(path: string, init: RequestInit, token: string | null) {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "Content-Type": "application/json",
    },
  });
}

export async function fetchWithAuth<T>(path: string, init: RequestInit = {}): Promise<T> {
  let token = await getIdToken();
  let resp = await authedFetch(path, init, token);

  if (resp.status === 401) {
    // Force a refresh and retry once.
    try {
      await fetchAuthSession({ forceRefresh: true });
      token = await getIdToken();
    } catch {
      token = null;
    }
    resp = await authedFetch(path, init, token);
  }

  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, body, `${resp.status} ${path}`);
  }

  return (await resp.json()) as T;
}
